import { CmsSectionShell } from './CmsSectionShell.tsx';
import { CmsSectionState } from './CmsSectionState.tsx';
import { SectionHeading } from '../SectionHeading.tsx';
import { themeOf, type ThemeClasses } from './cmsTheme.ts';
import { useFestivalData } from '../../lib/festivalData.ts';
import { cdnVariant, cdnSrcSet } from '../../lib/cdn.ts';
import type { SponsorsSection, Sponsor } from '../../lib/types.ts';

/**
 * Lists the festival sponsors (from sponsors.json), grouped by level and ordered by
 * displayWeight. Each sponsor is a uniform card in the section card shade with a square logo
 * tile on top and the name below — no eyebrows, counts, filters or tier colors. Cards link to
 * the sponsor's website when one is set.
 *
 * The logo sits on a translucent white tile rather than the card itself: partner logos are drawn
 * for white, and every section shade would otherwise tint them.
 */
export function CmsSponsorsSection({ theme, guitar }: SponsorsSection) {
  const t = themeOf(theme);
  const { data, loading, error } = useFestivalData();
  const levels = data ? [...data.sponsors.levels].sort((a, b) => a.displayWeight - b.displayWeight) : [];

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      <CmsSectionState
        loading={loading}
        error={!!error}
        loadingLabel="Indlæser partnere…"
        bodyClass={t.body}
        errorMessage="Partnerne kunne ikke indlæses."
      >
        <div className="space-y-10 md:space-y-14">
          {levels.map(level => (
            <div key={level.name}>
              <SectionHeading color={t.heading} eyebrowColor={t.eyebrow}>
                {level.name}
              </SectionHeading>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {level.sponsors.map(sponsor => (
                  <SponsorCard key={sponsor.name} sponsor={sponsor} t={t} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </CmsSectionState>
    </CmsSectionShell>
  );
}

function SponsorCard({ sponsor, t }: { sponsor: Sponsor; t: ThemeClasses }) {
  const hasLink = !!sponsor.websiteUrl;
  const inner = (
    <>
      <div className="aspect-square w-full rounded-cardSm overflow-hidden bg-white/60 flex items-center justify-center mb-4">
        {sponsor.logoUrl ? (
          <img
            src={cdnVariant(sponsor.logoUrl, 'mobile')}
            srcSet={cdnSrcSet(sponsor.logoUrl) || undefined}
            sizes="(min-width: 768px) 280px, 50vw"
            alt={sponsor.name}
            className="w-full h-full object-contain p-4"
            loading="lazy"
          />
        ) : (
          <span className="font-display text-mf-black/30 text-3xl">{sponsor.name.charAt(0)}</span>
        )}
      </div>
      <div className="font-display text-lg md:text-xl leading-tight">{sponsor.name}</div>
    </>
  );

  const cardClass = `block rounded-card p-4 md:p-6 transition-colors ${t.card.bg} ${t.card.text}`;

  return hasLink ? (
    <a
      href={sponsor.websiteUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`${cardClass} hover:opacity-90`}
    >
      {inner}
    </a>
  ) : (
    <div className={cardClass}>{inner}</div>
  );
}
